/**
 * API Client
 * Axios instance with Keycloak authentication and error handling
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { keycloak, refreshToken, getTenantId, initKeycloakSafe, keycloakInitOptions } from '../auth/keycloak'
import { API_CONFIG, BACKEND_MODE, resolveApiUrl } from './config'

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Request interceptor - Resolve cloud-direct URLs per-request
if (BACKEND_MODE === 'cloud-direct') {
  apiClient.interceptors.request.use(
    (config) => {
      // In cloud-direct mode, resolve each request URL to the correct service subdomain
      if (config.url && !config.url.startsWith('http')) {
        config.url = resolveApiUrl(config.url)
        config.baseURL = '' // Clear baseURL so resolved absolute URL is used
      }
      return config
    },
    (error) => Promise.reject(error)
  )
}

// Request interceptor - Add auth token and tenant ID.
// Awaits Keycloak init so requests fired during page load don't leak out
// without an Authorization header (race that produced 401 spam).
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Public pages (e.g. /versionsbuilt) must never trigger a Keycloak login.
      // keycloakInitOptions is onLoad:'login-required', so a stray axios call from a
      // global provider on a public route would full-redirect the SPA to the KC host.
      // Skip init on those paths — the request goes out unauthenticated (may 401) but
      // the page keeps rendering.
      const p = typeof window !== 'undefined' ? window.location.pathname : ''
      const isPublicPath = ['/signup', '/landing', '/p', '/versionsbuilt'].some(
        (x) => p === x || p.startsWith(x + '/')
      )
      if (!isPublicPath) {
        await initKeycloakSafe(keycloakInitOptions)
      }
    } catch {
      // init failed; continue without auth (will likely 401)
    }

    if (keycloak.authenticated) {
      await refreshToken(30)
      if (keycloak.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`
      }
      const tenantId = getTenantId()
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId
      }
    }

    config.headers['X-Correlation-ID'] = crypto.randomUUID()

    // MEDIA-UPLOAD FIX: the axios instance defaults Content-Type to
    // application/json (see axios.create above). When a call posts a FormData
    // body (file uploads) without explicitly overriding that header, axios v1
    // runs formDataToJSON() and strips the binary parts — the request goes out
    // as application/json and the file bytes never leave the browser (backend
    // records a 0-byte document). Deleting the header for FormData bodies lets
    // the browser set `multipart/form-data; boundary=…` itself.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers as unknown as { delete?: (n: string) => void } & Record<string, unknown>
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type')
      } else if (h) {
        delete h['Content-Type']
        delete h['content-type']
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Single-flight refresh promise for the 401 interceptor. Even though
// keycloak.updateToken() is itself dedupe-wrapped (see lib/auth/keycloak.ts),
// we still want one shared awaitable here so that N concurrent 401s share a
// single refresh AND retry off the same resolved token in lock-step.
let refreshInFlight: Promise<boolean> | null = null

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _retry503?: boolean }

    // Handle 401 - Try to refresh token (single-flight)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        if (!refreshInFlight) {
          refreshInFlight = refreshToken(0).finally(() => {
            refreshInFlight = null
          })
        }
        const refreshed = await refreshInFlight
        if (refreshed && keycloak.token) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${keycloak.token}`,
          }
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Redirect to login if refresh fails
        keycloak.login()
        return Promise.reject(refreshError)
      }
    }

    // Handle 403 - Forbidden
    if (error.response?.status === 403) {
      if (import.meta.env.DEV) console.warn('[API] 403 Forbidden:', error.config?.url)
    }

    // Handle 404 - Not found
    if (error.response?.status === 404) {
      if (import.meta.env.DEV) console.warn('[API] 404 Not found:', error.config?.url)
    }

    // Handle 503 - Service Unavailable (K8s pod restart) — auto-retry once
    if (error.response?.status === 503 && !originalRequest._retry503) {
      originalRequest._retry503 = true
      await new Promise((r) => setTimeout(r, 2000))
      return apiClient(originalRequest)
    }

    // Handle 500+ - Server errors (suppress noisy console in production)
    if (error.response?.status && error.response.status >= 500) {
      if (import.meta.env.DEV) console.warn('[API] Server error:', error.config?.url, error.response.status)
    }

    return Promise.reject(error)
  }
)

// API response types
export interface ApiResponse<T> {
  data: T
  message?: string
  status: number
}

export interface ApiError {
  message: string
  code?: string
  details?: Record<string, unknown>
  status: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface PaginationParams {
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// Generic request methods
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config)
  return response.data
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config)
  return response.data
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config)
  return response.data
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config)
  return response.data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<T>(url, config)
  return response.data
}

// File upload helper
export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
  fields?: Record<string, string | undefined | null>
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)
  // Append extra multipart form fields (e.g. a `caption`/`kind` for chat media).
  if (fields) {
    for (const [k, v] of Object.entries(fields)) {
      if (v !== undefined && v !== null && v !== '') formData.append(k, String(v))
    }
  }

  const response = await apiClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    },
  })

  return response.data
}

// File download helper
export async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await apiClient.get(url, {
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(link.href)
}

// Create service-specific client
export function createServiceClient(serviceBaseUrl: string): AxiosInstance {
  const serviceClient = axios.create({
    baseURL: serviceBaseUrl,
    timeout: API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })

  // Copy interceptors from main client
  const requestHandlers = apiClient.interceptors.request.handlers
  const requestHandler = requestHandlers.length > 0 ? requestHandlers[0] : undefined
  if (requestHandler) {
    serviceClient.interceptors.request.use(
      requestHandler.fulfilled,
      requestHandler.rejected
    )
  }

  const responseHandlers = apiClient.interceptors.response.handlers
  const responseHandler = responseHandlers.length > 0 ? responseHandlers[0] : undefined
  if (responseHandler) {
    serviceClient.interceptors.response.use(
      responseHandler.fulfilled,
      responseHandler.rejected
    )
  }

  return serviceClient
}

export default apiClient
